import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { getSiteCategories, FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

function CategoryCard({ cat, index }: { cat: FrontendCategory; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <Link
        to={`/category/${cat.slug}`}
        className="group block bg-white rounded-2xl p-5 md:p-6 border border-gray-100 hover:border-orange-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-orange-50 text-orange-500 shrink-0 group-hover:bg-orange-100 transition-colors">
            <i className={`${cat.icon || "ri-folder-line"} text-xl md:text-2xl`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 group-hover:text-orange-600 transition-colors">
              {cat.name}
            </h3>
            {cat.children && cat.children.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {cat.children.slice(0, 3).map((sub) => (
                  <span
                    key={sub.id}
                    className="px-2 py-0.5 text-xs bg-orange-50 text-orange-600 rounded-full"
                  >
                    {sub.name}
                  </span>
                ))}
                {cat.children.length > 3 && (
                  <span className="px-2 py-0.5 text-xs text-gray-400">
                    +{cat.children.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

export default function CategorySection() {
  const { siteId, loading: siteLoading } = useSite();
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const { ref: titleRef, isVisible: titleVisible } = useScrollAnimation({ threshold: 0.3 });

  useEffect(() => {
    // 如果站点还在加载或没有 siteId，不发送请求
    if (siteLoading || !siteId) {
      return;
    }

    getSiteCategories()
      .then((data) => {
        // 只取顶级分类（parent_id为null的）
        setCategories(data.filter((c) => !c.parent_id));
      })
      .catch(() => {
        // 加载失败时显示空
        setCategories([]);
      })
      .finally(() => setLoading(false));
  }, [siteId, siteLoading]);

  return (
    <section className="py-12 md:py-16 bg-[#FAF9F6]">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div 
            ref={titleRef}
            className={`text-center mb-8 md:mb-12 transition-all duration-600 ${
              titleVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              服务分类
            </h2>
            <p className="text-gray-500 text-sm md:text-base">
              涵盖各类商业服务，快速找到您需要的服务
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="animate-pulse bg-white rounded-2xl p-5 md:p-6 border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-200 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-24" />
                      <div className="h-3 bg-gray-200 rounded w-40" />
                      <div className="flex gap-1.5">
                        <div className="h-5 w-14 bg-gray-200 rounded-full" />
                        <div className="h-5 w-16 bg-gray-200 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : categories.length > 0 ? (
              categories.map((cat, index) => (
                <CategoryCard key={cat.id} cat={cat} index={index} />
              ))
            ) : (
              <div className="col-span-3 text-center py-10">
                <p className="text-gray-400">暂无服务分类</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
